import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  TextField, 
  Avatar, 
  Tabs, 
  Tab, 
  FormControlLabel, 
  Checkbox, 
  Switch, 
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  InputAdornment, 
  IconButton      
} from '@mui/material';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  getDocFromServer,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { registerPasskey } from '../utils/passkey';
import { normalizeLoginTimeRestriction } from '../utils/loginTimeRestriction';

// İkonlar
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SecurityIcon from '@mui/icons-material/Security';
import BadgeIcon from '@mui/icons-material/Badge';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import StoreIcon from '@mui/icons-material/Store';
import Visibility from '@mui/icons-material/Visibility';       
import VisibilityOff from '@mui/icons-material/VisibilityOff'; 

// Şube Listesi
const subeler = [
  { key: 'sefakoy_merkez', label: 'Sefaköy Merkez' },
  { key: 'sefakoy_sube', label: 'Sefaköy Şube' },
  { key: 'kavakli_sube', label: 'Kavaklı Şube' }
];

// Modül Listesi (GÜNCELLENDİ: ŞUBE EKLENDİ)
const anaModuller = [
  'YENİ İŞLEM', 
  'İŞLEMLER', 
  'MÜŞTERİLER', 
  'FİRMALAR', 
  'KASA', 
  'RAPORLAR',
  'ŞUBE', // <--- EKLENDİ
  'SARRAF', 
  'ÜRÜNLER',
  'YÖNETİCİ'
];

const HAFTA_GUNLERI = [
  { num: 1, label: 'Pazartesi' },
  { num: 2, label: 'Salı' },
  { num: 3, label: 'Çarşamba' },
  { num: 4, label: 'Perşembe' },
  { num: 5, label: 'Cuma' },
  { num: 6, label: 'Cumartesi' },
  { num: 7, label: 'Pazar' }
];

function PersonelYonetimi() {
  const [personeller, setPersoneller] = useState([]);
  const [selectedPersonel, setSelectedPersonel] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Şifre Göster/Gizle State'i
  const [showPassword, setShowPassword] = useState(false);

  // Form Verileri
  const [formData, setFormData] = useState({
    isim: '',
    soyisim: '',
    email: '',
    telefon: '',
    tcKimlik: '',
    adres: '',
    yeniSifre: '',
    rol: 'personel',
    aktif: true,
    passkeys: [],
    yetkiler: {
      subeler: [],
      moduller: [],
      ozelIzinler: {
        silme: false,
        raporGoruntuleme: false,
        stokDuzeltme: false,
        ozelFiyatGirme: false,
        finansGoruntuleme: false,
        finansMaliyetYonetimi: false
      }
    },
    loginTimeRestriction: normalizeLoginTimeRestriction()
  });

  const fetchPersoneller = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'personeller'));
      /** dokümandaki id alanı varsa ...doc.data() gerçek doc.id'yi ezerdi; hep Firestore kesin kimliği kullan */
      const list = querySnapshot.docs.map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }));
      setPersoneller(list);
      return list;
    } catch (error) {
      console.error("Hata:", error);
      setMessage({ type: 'error', text: 'Personel listesi alınamadı.' });
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPersoneller();
  }, []);

  const applyPersonelToForm = (personel, { resetTab = true } = {}) => {
    const docId = String(personel?.id ?? '').trim();
    setSelectedPersonel(personel);
    setShowPassword(false);
    setFormData({
      ...personel,
      id: docId || personel?.id,
      /** Mevcut şifre asla forma yüklenmez/gösterilmez — sadece yeni şifre girilebilir. */
      yeniSifre: '',
      yetkiler: {
        subeler: personel.yetkiler?.subeler || [],
        moduller: personel.yetkiler?.moduller || [],
        ozelIzinler: personel.yetkiler?.ozelIzinler || {}
      },
      passkeys: personel.passkeys || [],
      loginTimeRestriction: normalizeLoginTimeRestriction(personel.loginTimeRestriction)
    });
    if (resetTab) setActiveTab(0);
    setMessage({ type: '', text: '' });
  };

  /** Güvenlik şifresi dahil — listedeki cache yerine Firestore’daki güncel doküman */
  const fetchFreshPersonelById = async (personelOrId) => {
    const selectedId = String(
      typeof personelOrId === 'string' ? personelOrId : personelOrId?.id ?? ''
    ).trim();
    if (!selectedId) return null;
    try {
      const ref = doc(db, 'personeller', selectedId);
      let snap;
      try {
        snap = await getDocFromServer(ref);
      } catch {
        snap = await getDoc(ref);
      }
      if (!snap.exists()) {
        return typeof personelOrId === 'object' ? personelOrId : null;
      }
      return { ...snap.data(), id: snap.id };
    } catch (error) {
      console.error('Personel taze veri alınamadı:', error);
      return typeof personelOrId === 'object' ? personelOrId : null;
    }
  };

  const handleNewPersonel = () => {
    setSelectedPersonel(null);
    setShowPassword(false); 
    setFormData({
      isim: '',
      soyisim: '',
      email: '',
      telefon: '',
      tcKimlik: '',
      adres: '',
      yeniSifre: '',
      rol: 'personel',
      aktif: true,
      passkeys: [],
      yetkiler: {
        subeler: [],
        moduller: [],
        ozelIzinler: {
          silme: false,
          raporGoruntuleme: false,
          stokDuzeltme: false,
          ozelFiyatGirme: false,
          finansGoruntuleme: false,
          finansMaliyetYonetimi: false
        }
      },
      loginTimeRestriction: normalizeLoginTimeRestriction()
    });
    setActiveTab(0);
    setMessage({ type: '', text: '' });
  };

  const handleSelectPersonel = async (personel) => {
    const selectedId = String(personel?.id ?? '').trim();
    if (!selectedId) return;

    try {
      const ref = doc(db, 'personeller', selectedId);
      let freshSnap;
      try {
        freshSnap = await getDocFromServer(ref);
      } catch {
        freshSnap = await getDoc(ref);
      }
      const freshPersonel = freshSnap.exists()
        ? { ...freshSnap.data(), id: freshSnap.id }
        : { ...personel, id: selectedId };

      applyPersonelToForm(freshPersonel, { resetTab: true });
    } catch (error) {
      console.error('Personel taze veri alınamadı:', error);
      applyPersonelToForm({ ...personel, id: selectedId }, { resetTab: true });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubeToggle = (subeKey) => {
    setFormData(prev => {
      const currentSubeler = prev.yetkiler.subeler || [];
      const newSubeler = currentSubeler.includes(subeKey)
        ? currentSubeler.filter(k => k !== subeKey)
        : [...currentSubeler, subeKey];
      return { ...prev, yetkiler: { ...prev.yetkiler, subeler: newSubeler } };
    });
  };

  const handleModuleToggle = (modulName) => {
    setFormData(prev => {
      const currentModuller = prev.yetkiler.moduller || [];
      const newModuller = currentModuller.includes(modulName)
        ? currentModuller.filter(m => m !== modulName)
        : [...currentModuller, modulName];
      return { ...prev, yetkiler: { ...prev.yetkiler, moduller: newModuller } };
    });
  };

  const handlePermissionToggle = (permissionKey) => {
    setFormData(prev => ({
      ...prev,
      yetkiler: {
        ...prev.yetkiler,
        ozelIzinler: {
          ...prev.yetkiler.ozelIzinler,
          [permissionKey]: !prev.yetkiler.ozelIzinler?.[permissionKey]
        }
      }
    }));
  };

  const handleLoginRestrictionField = (patch) => {
    setFormData((prev) => {
      const cur = normalizeLoginTimeRestriction(prev.loginTimeRestriction);
      return {
        ...prev,
        loginTimeRestriction: normalizeLoginTimeRestriction({ ...cur, ...patch })
      };
    });
  };

  const handleLoginRestrictionDayToggle = (dayNum) => {
    setFormData((prev) => {
      const base = normalizeLoginTimeRestriction(prev.loginTimeRestriction);
      const set = new Set(base.allowedDays);
      if (set.has(dayNum)) set.delete(dayNum);
      else set.add(dayNum);
      const allowedDays = [...set].sort((a, b) => a - b);
      return {
        ...prev,
        loginTimeRestriction: { ...base, allowedDays: allowedDays.length ? allowedDays : [1, 2, 3, 4, 5, 6] }
      };
    });
  };

  const handleSave = async () => {
    if (!formData.isim || !formData.soyisim || !formData.email) {
      setMessage({ type: 'error', text: 'Lütfen zorunlu alanları doldurun.' });
      return;
    }
    const isNewPersonel = !selectedPersonel;
    const newPasswordInput = String(formData.yeniSifre || '').trim();
    if (isNewPersonel && !newPasswordInput) {
      setMessage({ type: 'error', text: 'Yeni personel için bir giriş şifresi belirlemelisiniz.' });
      return;
    }
    setLoading(true);
    /** Kayıttan sonra formu yenilemek için; selectedPersonel state erken yakalanır */
    const wasEditingExisting = Boolean(selectedPersonel);
    const tabKeeper = activeTab;
    let hydrateDocId = '';
    let personIdForPasswordWrite = '';

    try {
      const loginTimeRestriction = normalizeLoginTimeRestriction(formData.loginTimeRestriction);
      const formWithoutId = { ...formData };
      delete formWithoutId.id;
      /** Şifre artık kök dokümanda tutulmaz — ayrı, istemciden okunamayan bir alt dokümanda. */
      delete formWithoutId.yeniSifre;
      delete formWithoutId.sifre;
      const saveData = {
        ...formWithoutId,
        loginTimeRestriction,
        sonGuncelleme: serverTimestamp()
      };
      if (selectedPersonel) {
        const personnelDocId = String(selectedPersonel.id ?? '').trim();
        if (!personnelDocId) {
          console.warn('[personelyonetimi] Güncelleme iptal: selectedPersonel.id yok.', {
            selectedName: `${formData.isim ?? ''} ${formData.soyisim ?? ''}`.trim()
          });
          setMessage({
            type: 'error',
            text: 'Personel güncellenemedi: kayıt kimliği eksik. Listeyi yenileyip personeli tekrar seçin.'
          });
          setLoading(false);
          return;
        }
        // Eski kayıtlarda kök dokümanda kalmış olabilecek düz metin şifreyi fırsat buldukça temizle.
        saveData.sifre = deleteField();
        await updateDoc(doc(db, 'personeller', personnelDocId), saveData);
        hydrateDocId = personnelDocId;
        personIdForPasswordWrite = personnelDocId;
      } else {
        saveData.olusturmaTarihi = serverTimestamp();
        const newRef = await addDoc(collection(db, 'personeller'), saveData);
        personIdForPasswordWrite = newRef.id;
        setMessage({ type: 'success', text: 'Oluşturuldu.' });
      }

      if (newPasswordInput) {
        await setDoc(
          doc(db, 'personeller', personIdForPasswordWrite, 'private', 'auth'),
          {
            sifre: newPasswordInput,
            failedAttempts: 0,
            lockedUntil: deleteField(),
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
      }

      await fetchPersoneller();

      if (hydrateDocId) {
        const freshPersonel = await fetchFreshPersonelById(hydrateDocId);
        if (freshPersonel) {
          applyPersonelToForm(freshPersonel, { resetTab: false });
          setActiveTab(tabKeeper);
        }
        setMessage({ type: 'success', text: 'Güncellendi.' });
      }

      if (!wasEditingExisting) handleNewPersonel();
    } catch (error) {
      setMessage({ type: 'error', text: 'Hata: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPersonel || !window.confirm('Silmek istediğinize emin misiniz?')) return;
    try {
      await deleteDoc(doc(db, 'personeller', selectedPersonel.id));
      setMessage({ type: 'success', text: 'Personel silindi.' });
      fetchPersoneller();
      handleNewPersonel();
    } catch (error) {
      setMessage({ type: 'error', text: 'Silme hatası: ' + error.message });
    }
  };

  // Şifre Göster/Gizle Tetikleyicisi
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();

  return (
    <Box
      className="py-page"
      sx={{
        display: 'flex',
        height: '70vh',
        gap: 1.5,
        p: 1,
        bgcolor: 'linear-gradient(165deg, #f5f2eb 0%, #ece6da 45%, #e2dacd 100%)',
        background: 'linear-gradient(165deg, #f5f2eb 0%, #ece6da 45%, #e2dacd 100%)'
      }}
    >
      
      {/* SOL TARAFTAKİ LİSTE */}
      <Paper
        className="py-sidebar"
        elevation={0}
        sx={{
          width: '280px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2.5,
          border: '1px solid rgba(212,175,98,0.25)',
          boxShadow: '0 10px 28px rgba(15,23,42,0.14)'
        }}
      >
        <Box className="py-sidebar-header" sx={{ p: 1.4, background: 'linear-gradient(180deg, #111827 0%, #0b1220 100%)', color: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight="bold">Personeller</Typography>
          <Button 
            className="py-new-btn"
            variant="contained" 
            size="small" 
            startIcon={<PersonAddIcon />}
            onClick={handleNewPersonel}
            sx={{
              background: 'linear-gradient(180deg, #1f2937 0%, #0f172a 100%)',
              border: '1px solid rgba(212,175,98,0.42)',
              '&:hover': { background: 'linear-gradient(180deg, #273549 0%, #152235 100%)' },
              fontSize: '0.72rem'
            }}
          >
            Yeni
          </Button>
        </Box>
        <List className="py-sidebar-list" sx={{ flex: 1, overflowY: 'auto' }}>
          {personeller.map((personel) => (
            <React.Fragment key={personel.id}>
              <ListItem 
                button 
                selected={selectedPersonel?.id === personel.id}
                onClick={() => handleSelectPersonel(personel)}
                sx={{
                  '&.Mui-selected': { bgcolor: 'rgba(212,175,98,0.14)', borderLeft: '3px solid #8a6a1f' },
                  '&:hover': { bgcolor: 'rgba(148,163,184,0.12)' },
                  py: 1
                }}
              >
                <Avatar sx={{ mr: 2, bgcolor: '#0f172a', width: 32, height: 32, fontSize: 14, border: '1px solid rgba(212,175,98,0.45)' }}>
                  {personel.isim.charAt(0)}{personel.soyisim.charAt(0)}
                </Avatar>
                <ListItemText 
                  primary={`${personel.isim} ${personel.soyisim}`} 
                  secondary={personel.rol === 'admin' ? 'Yönetici' : 'Personel'} 
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 'bold' }}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* SAĞ TARAFTAKİ DETAY PANELİ */}
      <Paper
        className="py-detail"
        elevation={0}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2.5,
          border: '1px solid rgba(212,175,98,0.25)',
          boxShadow: '0 12px 30px rgba(15,23,42,0.14)'
        }}
      >
        
        {/* Üst Başlık */}
        <Box className="py-detail-header" sx={{ p: 1.4, borderBottom: '1px solid rgba(212,175,98,0.25)', background: 'linear-gradient(180deg, #fcfbf8 0%, #f3efe7 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold" color="#0f172a" fontSize="1.02rem">
            {selectedPersonel ? `${formData.isim} ${formData.soyisim}` : 'Yeni Personel Kartı'}
          </Typography>
          <Box className="py-detail-actions">
            {selectedPersonel && (
              <Button 
                variant="outlined" 
                color="error" 
                size="small"
                startIcon={<DeleteIcon />} 
                onClick={handleDelete}
                sx={{ mr: 1.2, borderColor: 'rgba(220,38,38,0.45)', color: '#b91c1c' }}
              >
                Sil
              </Button>
            )}
            <Button 
              variant="contained" 
              size="small"
              startIcon={<SaveIcon />} 
              onClick={handleSave}
              disabled={loading}
              sx={{
                background: 'linear-gradient(180deg, #1f2937 0%, #0f172a 100%)',
                border: '1px solid rgba(212,175,98,0.4)',
                '&:hover': { background: 'linear-gradient(180deg, #273549 0%, #152235 100%)' }
              }}
            >
              {loading ? '...' : 'Kaydet'}
            </Button>
          </Box>
        </Box>

        {message.text && (
          <Alert className="py-alert" severity={message.type} sx={{ m: 1, py: 0 }} onClose={() => setMessage({ type: '', text: '' })}>
            {message.text}
          </Alert>
        )}

        <Tabs 
          className="py-tabs"
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)} 
          indicatorColor="primary" 
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: '1px solid rgba(212,175,98,0.22)', px: 1.5, minHeight: '46px', background: 'rgba(248,250,252,0.9)' }}
        >
          <Tab icon={<BadgeIcon />} iconPosition="start" label="KİMLİK" sx={{ minHeight: '46px', fontSize: '0.78rem', fontWeight: 700 }} />
          <Tab icon={<FolderSharedIcon />} iconPosition="start" label="ÖZLÜK" sx={{ minHeight: '46px', fontSize: '0.78rem', fontWeight: 700 }} />
          <Tab icon={<StoreIcon />} iconPosition="start" label="YETKİLER" sx={{ minHeight: '46px', fontSize: '0.78rem', fontWeight: 700 }} />
          <Tab icon={<SecurityIcon />} iconPosition="start" label="GÜVENLİK" sx={{ minHeight: '46px', fontSize: '0.78rem', fontWeight: 700 }} />
        </Tabs>

        <Box className="py-tab-content" sx={{ p: 1.8, flex: 1, overflowY: 'auto', background: 'linear-gradient(180deg, #fcfbf8 0%, #f5f1eb 100%)' }}>
          
          {/* SEKME 1: KİMLİK */}
          {activeTab === 0 && (
            <Grid container spacing={2} className="py-identity-grid">
              <Grid item xs={12} sm={6}>
                <TextField label="İsim" name="isim" value={formData.isim} onChange={handleChange} fullWidth required size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Soyisim" name="soyisim" value={formData.soyisim} onChange={handleChange} fullWidth required size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="E-posta" name="email" value={formData.email} onChange={handleChange} fullWidth required size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Telefon" name="telefon" value={formData.telefon} onChange={handleChange} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="TC Kimlik No" name="tcKimlik" value={formData.tcKimlik} onChange={handleChange} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Adres" name="adres" value={formData.adres} onChange={handleChange} fullWidth multiline rows={2} size="small" />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={formData.aktif} onChange={(e) => setFormData({...formData, aktif: e.target.checked})} />}
                  label="Personel Aktif (Giriş Yapabilir)"
                />
              </Grid>
            </Grid>
          )}

          {/* SEKME 2: ÖZLÜK */}
          {activeTab === 1 && (
            <Box textAlign="center" py={3}>
              <FolderSharedIcon sx={{ fontSize: 50, color: '#ccc' }} />
              <Typography variant="body1" color="textSecondary" mt={1}>
                Evrak Yönetimi
              </Typography>
              <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} sx={{ mt: 2 }}>
                Dosya Yükle
              </Button>
            </Box>
          )}

          {/* SEKME 3: YETKİLER */}
          {activeTab === 2 && (
            <Grid container spacing={2} className="py-permissions-grid">
              
              {/* 1. SÜTUN: ŞUBELER */}
              <Grid item xs={12} sm={6} xl={3}>
                <Typography variant="subtitle2" gutterBottom color="#0f172a" fontWeight="bold">1. Şube Erişimi</Typography>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8fafc', borderColor: 'rgba(212,175,98,0.24)', height: '100%' }}>
                  {subeler.map(sube => (
                    <Box key={sube.key}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            size="small"
                            checked={formData.yetkiler.subeler?.includes(sube.key)}
                            onChange={() => handleSubeToggle(sube.key)}
                          />
                        }
                        label={<Typography fontSize="0.85rem">{sube.label}</Typography>}
                      />
                    </Box>
                  ))}
                </Paper>
              </Grid>

              {/* 2. SÜTUN: MODÜLLER */}
              <Grid item xs={12} sm={6} xl={3}>
                <Typography variant="subtitle2" gutterBottom color="#0f172a" fontWeight="bold">2. Menü/Modül Erişimi</Typography>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8fafc', borderColor: 'rgba(212,175,98,0.24)', height: '100%' }}>
                  {anaModuller.map(modul => (
                    <Box key={modul}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            size="small"
                            checked={formData.yetkiler.moduller?.includes(modul)}
                            onChange={() => handleModuleToggle(modul)}
                          />
                        }
                        label={<Typography fontSize="0.85rem">{modul}</Typography>}
                      />
                    </Box>
                  ))}
                </Paper>
              </Grid>
              
              {/* 3. SÜTUN: ÖZEL İZİNLER */}
              <Grid item xs={12} sm={6} xl={3}>
                <Typography variant="subtitle2" gutterBottom color="#0f172a" fontWeight="bold">3. Özel İşlem İzinleri</Typography>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8fafc', borderColor: 'rgba(212,175,98,0.24)', height: '100%' }}>
                  <Box><FormControlLabel control={<Switch size="small" checked={formData.yetkiler.ozelIzinler?.silme} onChange={() => handlePermissionToggle('silme')} />} label={<Typography fontSize="0.85rem">Silme (Çöp Kutusu)</Typography>} /></Box>
                  <Box><FormControlLabel control={<Switch size="small" checked={formData.yetkiler.ozelIzinler?.raporGoruntuleme} onChange={() => handlePermissionToggle('raporGoruntuleme')} />} label={<Typography fontSize="0.85rem">Rapor Görme</Typography>} /></Box>
                  <Box><FormControlLabel control={<Switch size="small" checked={formData.yetkiler.ozelIzinler?.stokDuzeltme} onChange={() => handlePermissionToggle('stokDuzeltme')} />} label={<Typography fontSize="0.85rem">Stok Düzeltme</Typography>} /></Box>
                  <Box><FormControlLabel control={<Switch size="small" checked={formData.yetkiler.ozelIzinler?.ozelFiyatGirme} onChange={() => handlePermissionToggle('ozelFiyatGirme')} />} label={<Typography fontSize="0.85rem">Manuel Fiyat</Typography>} /></Box>
                </Paper>
              </Grid>

              {/* 4. SÜTUN: SAAT KISITLAMASI */}
              <Grid item xs={12} sm={6} xl={3}>
                <Typography variant="subtitle2" gutterBottom color="#0f172a" fontWeight="bold">
                  4. Saat Kısıtlaması
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: '#f8fafc', borderColor: 'rgba(212,175,98,0.24)', height: '100%' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={Boolean(formData.loginTimeRestriction?.enabled)}
                        onChange={(e) => handleLoginRestrictionField({ enabled: e.target.checked })}
                      />
                    }
                    label={<Typography fontSize="0.85rem">Saat kısıtlaması aktif</Typography>}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, mb: 1 }}>
                    <TextField
                      label="Başlangıç"
                      type="time"
                      size="small"
                      value={formData.loginTimeRestriction?.startTime || '09:00'}
                      onChange={(e) => handleLoginRestrictionField({ startTime: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 120, '& input': { fontSize: '0.85rem' } }}
                      disabled={!formData.loginTimeRestriction?.enabled}
                    />
                    <TextField
                      label="Bitiş"
                      type="time"
                      size="small"
                      value={formData.loginTimeRestriction?.endTime || '19:30'}
                      onChange={(e) => handleLoginRestrictionField({ endTime: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 120, '& input': { fontSize: '0.85rem' } }}
                      disabled={!formData.loginTimeRestriction?.enabled}
                    />
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.75 }}>
                    Günler {formData.loginTimeRestriction?.enabled ? '' : '(pasifken uygulanmaz)'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.35 }}>
                    {HAFTA_GUNLERI.map(({ num, label }) => (
                      <FormControlLabel
                        key={num}
                        control={
                          <Checkbox
                            size="small"
                            checked={Boolean(formData.loginTimeRestriction?.allowedDays?.includes(num))}
                            onChange={() => handleLoginRestrictionDayToggle(num)}
                            disabled={!formData.loginTimeRestriction?.enabled}
                          />
                        }
                        label={<Typography fontSize="0.78rem">{label}</Typography>}
                        sx={{ mr: 0.5, mb: 0 }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* SEKME 4: GÜVENLİK (GÖZ İKONLU ŞİFRE KUTUSU) */}
          {activeTab === 3 && (
            <Box>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Yeni Şifre"
                    name="yeniSifre"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.yeniSifre || ''}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    autoComplete="new-password"
                    helperText={
                      selectedPersonel
                        ? 'Mevcut şifre görüntülenemez (güvenlik). Değiştirmek için yeni şifre girin, boş bırakırsanız mevcut şifre korunur.'
                        : 'Bu personel için bir giriş şifresi belirleyin.'
                    }
                    InputProps={{ 
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="şifreyi göster/gizle"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Divider sx={{ my: 2, borderColor: 'rgba(212,175,98,0.3)' }} />
              <Typography variant="subtitle1" color="#0f172a" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FingerprintIcon /> Biyometrik Giriş (Passkeys)
              </Typography>
              <Paper className="py-passkey-row" variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f8fafc', borderColor: 'rgba(212,175,98,0.24)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography fontWeight="bold" fontSize="0.9rem">Kayıtlı Cihazlar</Typography>
                  <Typography variant="caption">
                    {formData.passkeys?.length > 0 
                      ? `${formData.passkeys.length} adet cihaz tanımlı.` 
                      : "Cihaz yok."}
                  </Typography>
                </Box>
                {selectedPersonel && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<FingerprintIcon />}
                    sx={{
                      background: 'linear-gradient(180deg, #1f2937 0%, #0f172a 100%)',
                      border: '1px solid rgba(212,175,98,0.38)',
                      '&:hover': { background: 'linear-gradient(180deg, #273549 0%, #152235 100%)' }
                    }}
                    onClick={async () => {
                       try {
                         const passkeyId = await registerPasskey(formData.email);
                         const currentKeys = formData.passkeys || [];
                         const newKeys = [...currentKeys, passkeyId];
                         setFormData(prev => ({ ...prev, passkeys: newKeys }));
                         alert("Cihaz tanındı! 'Kaydet' butonuna basınız.");
                       } catch (err) {
                         alert("Hata: " + err.message);
                       }
                    }}
                  >
                    Cihaz Tanımla +
                  </Button>
                )}
              </Paper>
              {formData.passkeys?.length > 0 && (
                <Button 
                  size="small" 
                  color="error" 
                  sx={{ mt: 1 }}
                  onClick={() => {
                    if(window.confirm('Sıfırlamak istiyor musunuz?')) {
                      setFormData(prev => ({ ...prev, passkeys: [] }));
                    }
                  }}
                >
                  Sıfırla
                </Button>
              )}
            </Box>
          )}

        </Box>
      </Paper>
      <style>{`
        /* --- MOBİL: Personel Yönetimi (masaüstü sx stillerine dokunulmaz) --- */
        @media (max-width: 1649px), (max-width: 1400px) and (any-pointer: coarse) {
          .py-page {
            flex-direction: column !important;
            height: auto !important;
            min-height: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow-x: hidden !important;
            padding: 6px 2px 80px !important;
            box-sizing: border-box !important;
            gap: 12px !important;
          }

          .py-sidebar {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            height: auto !important;
            max-height: none !important;
            flex-shrink: 0 !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
          }

          .py-sidebar-header {
            padding: 10px 12px !important;
            flex-shrink: 0 !important;
          }

          .py-new-btn {
            min-height: 36px !important;
          }

          .py-sidebar-list {
            flex: none !important;
            min-height: 168px !important;
            max-height: min(40vh, 300px) !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            -webkit-overflow-scrolling: touch;
          }

          .py-sidebar-list .MuiListItem-root {
            min-height: 56px !important;
          }

          .py-detail {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            flex: none !important;
            overflow: visible !important;
            display: flex !important;
            flex-direction: column !important;
          }

          .py-detail-header {
            flex-direction: row !important;
            flex-wrap: wrap !important;
            align-items: center !important;
            gap: 8px !important;
            padding: 10px 12px !important;
            background: linear-gradient(180deg, #fcfbf8 0%, #f3efe7 100%) !important;
          }

          .py-detail-header .MuiTypography-h6 {
            flex: 1 1 140px !important;
            min-width: 0 !important;
            font-size: 0.95rem !important;
            line-height: 1.3 !important;
          }

          .py-detail-actions {
            display: flex !important;
            flex-wrap: nowrap !important;
            gap: 8px !important;
            width: auto !important;
            flex: 0 0 auto !important;
            margin-left: auto !important;
          }

          .py-detail-actions .MuiButton-root {
            flex: 0 0 auto !important;
            width: auto !important;
            min-width: 0 !important;
            min-height: 36px !important;
            margin-right: 0 !important;
            padding-left: 10px !important;
            padding-right: 10px !important;
            font-size: 0.78rem !important;
          }

          .py-detail-actions .MuiButton-startIcon {
            margin-right: 4px !important;
          }

          .py-tabs {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            overflow: hidden !important;
          }

          .py-tabs .MuiTabs-root {
            min-height: 42px !important;
          }

          .py-tabs .MuiTabs-scroller {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
          }

          .py-tabs .MuiTabs-flexContainer {
            flex-wrap: nowrap !important;
          }

          .py-tabs .MuiTab-root {
            min-width: 0 !important;
            flex: 0 0 auto !important;
            min-height: 42px !important;
            font-size: 0.62rem !important;
            font-weight: 700 !important;
            padding: 6px 10px !important;
            letter-spacing: 0 !important;
          }

          .py-tabs .MuiTab-iconWrapper {
            display: none !important;
          }

          .py-tab-content {
            padding: 12px !important;
            overflow: visible !important;
            flex: none !important;
          }

          .py-tab-content .MuiInputBase-input {
            font-size: 16px !important;
          }

          .py-identity-grid > .MuiGrid-item {
            width: 100% !important;
            max-width: 100% !important;
            flex-basis: 100% !important;
          }

          .py-permissions-grid > .MuiGrid-item {
            width: 100% !important;
            max-width: 100% !important;
            flex-basis: 100% !important;
          }

          .py-passkey-row {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }

          .py-passkey-row .MuiButton-root {
            width: 100% !important;
            min-height: 40px !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default PersonelYonetimi;